/** How the manual key is grouped for transcription. */
const GROUP_SIZE = 4;

/**
 * Formats a TOTP shared secret for display, in space-separated groups of four.
 *
 * - Ported from the original's `insertCharAfterEachNthChar`, minus its size and separator arguments:
 *   one caller, one grouping, and a step of zero would have looped forever.
 * - Matters more than it looks — the fallback for anyone who cannot scan the QR is typing a
 *   ~32-character base32 string by hand, and unbroken it is near-impossible to transcribe correctly.
 */
export const chunkSecret = (secret: string): string => {
  const groups: string[] = [];
  for (let start = 0; start < secret.length; start += GROUP_SIZE) {
    groups.push(secret.slice(start, start + GROUP_SIZE));
  }
  return groups.join(' ');
};

/** What an authenticator app shows as the account name. */
const ISSUER = 'Keboola Developer Portal';

/**
 * The `otpauth://` URI an authenticator app scans.
 *
 * - Both the label and the issuer are percent-encoded — an email is safe but the issuer contains
 *   spaces, and an unencoded one produces a URI some apps silently refuse.
 * - ⚠️ Built by hand rather than with `URLSearchParams`, whose `toString()` form-encodes a space as
 *   `+`. That is an HTML form convention; `otpauth://` is read as an RFC 3986 URI, where `+` is a
 *   literal — so a form-encoded issuer displays as "Keboola+Developer+Portal" in the authenticator.
 */
export const buildOtpauthUri = (email: string, secret: string): string => {
  const label = encodeURIComponent(email);
  const query = `secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(ISSUER)}`;
  return `otpauth://totp/${label}?${query}`;
};
